import React, { useEffect, useState } from "react";
import {
  mockMeta,
  mockPriorities,
  mockWeather,
  mockGrass,
  mockHealth,
  mockSlurry,
  mockMarket,
  mockPolicy,
  mockTwinSignals,
  mockDeadlines,
} from "../assets/mock-data/FarmTwinDailyMockData.js";

import {
  getDailySummary,
  getWeatherConditions,
  getGrassForage,
  getHealthRisk,
  getSlurryWindow,
  getMarket,
  getPolicySignals,
  getTwinSignals,
  getDeadlines,
} from "../api/FarmTwinDailyApiClient.js";

import styles from '../assets/styles/FarmTwinDailyDashboard.module.css';

function PrioritySummary({ dateLabel, priorities }) {
  return (
    <section className={styles.prioritySummary}>
      <div className={styles.prioritySummaryHeader}>
        <div className={styles.priorityTitle}>Today&apos;s priorities</div>
        <div className={styles.priorityDate}>{dateLabel}</div>
      </div>
      <ul className={styles.priorityList}>
        {priorities.map((item, idx) => (
          <li key={idx} className={styles.priorityItem}>
            <span className={styles.priorityBullet} />
            <span className={styles.priorityText}>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function WeatherCard({ data }) {
  const { location, today, fieldConditions } = data;
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Weather & Field Conditions</div>
        <div className={styles.cardTag}>48Hour outlook</div>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.metricRow}>
          <div className={styles.metricLabel}>Location</div>
          <div className={styles.metricValue}>{location}</div>
        </div>
        <div className={styles.metricRow}>
          <div className={styles.metricLabel}>Today</div>
          <div className={styles.metricValue}>
            {today.maxTemp} / {today.minTemp}, {today.rainMm} mm rain,{" "}
            {today.wind}, frost risk {today.frostRisk}
          </div>
        </div>
        <p className={styles.subtitle} style={{ marginTop: 6 }}>
          Field conditions
        </p>
        <ul className={styles.listCompact}>
          {fieldConditions.map((f) => (
            <li key={f.label}>
              <span>{f.label}</span>
              <span>
                {f.status}
                <span
                  className={`${styles.metricBadge} ${
                    f.risk === "risk"
                      ? styles.metricPillRisk
                      : f.risk === "warn"
                      ? styles.metricPillWarn
                      : styles.metricPillOk
                  }`}
                >
                  {f.risk === "risk"
                    ? "High risk"
                    : f.risk === "warn"
                    ? "Caution"
                    : "OK"}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function GrassCard({ data }) {
  const {
    region,
    currentGrowth,
    avg7d,
    dmEstimate,
    grazingDaysAhead,
    note,
  } = data;
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Grass Growth & Forage</div>
        <div className={styles.cardTag}>Past 7 days</div>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Region</span>
          <span className={styles.metricValue}>{region}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Current growth</span>
          <span className={styles.metricValue}>{currentGrowth}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>7-day average</span>
          <span className={styles.metricValue}>{avg7d}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Grass DM estimate</span>
          <span className={styles.metricValue}>{dmEstimate}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Estimated grazing buffer</span>
          <span className={styles.metricValue}>{grazingDaysAhead} days</span>
        </div>
        <p className={styles.subtitle} style={{ marginTop: 6 }}>
          Advisory
        </p>
        <p style={{ fontSize: "0.8rem", marginBottom: "0px"}}>{note}</p>
      </div>
    </section>
  );
}

function HealthCard({ data }) {
  const { region, parasiteRisk, flukeRiskLevel, heatStress, chillRisk } =
    data;
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Livestock Health & Welfare</div>
        <div className={styles.cardTag}>Risk outlook</div>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Region</span>
          <span className={styles.metricValue}>{region}</span>
        </div>
        <p className={styles.subtitle} style={{ marginTop: 6 }}>
          Key risks
        </p>
        <ul className={styles.listCompact}>
          <li>
            <span>Parasites</span>
            <span>{parasiteRisk}</span>
          </li>
          <li>
            <span>Fluke</span>
            <span>
              {flukeRiskLevel}
              <span className={`${styles.metricBadge} ${styles.metricPillRisk}`}>High</span>
            </span>
          </li>
          <li>
            <span>Heat stress (cattle)</span>
            <span>
              {heatStress}
              <span className={`${styles.metricBadge} ${styles.metricPillOk}`}>Low</span>
            </span>
          </li>
          <li>
            <span>Chill risk (young lambs)</span>
            <span>
              {chillRisk}
              <span className={`${styles.metricBadge} ${styles.metricPillWarn}`}>Medium</span>
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

function SlurryCard({ data }) {
  const {
    nvzStatus,
    suitability,
    runoffRisk,
    storeFillPercent,
    daysUntilFull,
  } = data;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Manure & Slurry Window</div>
        <div className={styles.cardTag}>Nutrient compliance</div>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>NVZ status</span>
          <span className={styles.metricValue}>{nvzStatus}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Spreading suitability</span>
          <span className={styles.metricValue}>
            {suitability}
            <span className={`${styles.metricBadge} ${styles.metricPillWarn}`}>Caution</span>
          </span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Runoff risk</span>
          <span className={styles.metricValue}>
            {runoffRisk}
            <span className={`${styles.metricBadge} ${styles.metricPillRisk}`}>High risk</span>
          </span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Slurry store fill</span>
          <span className={styles.metricValue}>
            {storeFillPercent}% ({daysUntilFull} days at current inflow)
          </span>
        </div>
      </div>
    </section>
  );
}

function MarketCard({ data }) {
  const { beef, lamb } = data;

  const beefTrendClass =
    beef.trend === "up"
      ? `${styles.trendUp}`
      : beef.trend === "down"
      ? `${styles.down}`
      : `${styles.trendFlat}`;

  const lambTrendClass =
    lamb.trend === "up"
      ? `${styles.trendUp}`
      : lamb.trend === "down"
      ? `${styles.down}`
      : `${styles.trendFlat}`;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Market & Price Forecast</div>
        <div className={styles.cardTag}>7-day moving averages</div>
      </header>
      <div className={styles.cardBody}>
        <p className={styles.subtitle}>Beef</p>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>{beef.classLabel}</span>
          <span className={styles.metricValue}>
            Last: {beef.lastPrices[beef.lastPrices.length - 1].price} p/kg
          </span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>MA7</span>
          <span className={styles.metricValue}>{beef.ma7} p/kg</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>3-day forecast</span>
          <span className={styles.metricValue}>
            {beef.forecast.min}–{beef.forecast.max} p/kg{" "}
            <span className={beefTrendClass}>
              {beef.trend === "up"
                ? "Rising"
                : beef.trend === "down"
                ? "Falling"
                : "Stable"}
            </span>
          </span>
        </div>
        <p style={{ fontSize: "0.78rem", marginTop: 4, marginBottom: "0px"}}>{beef.comment}</p>

        <p className={styles.subtitle} style={{ marginTop: 10 }}>
          Lamb
        </p>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>{lamb.classLabel}</span>
          <span className={styles.metricValue}>
            Last: {lamb.lastPrice} p/kg
          </span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>MA7</span>
          <span className={styles.metricValue}>{lamb.ma7} p/kg</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>3-day forecast</span>
          <span className={styles.metricValue}>
            {lamb.forecast.min}–{lamb.forecast.max} p/kg{" "}
            <span className={lambTrendClass}>
              {lamb.trend === "up"
                ? "Rising"
                : lamb.trend === "down"
                ? "Falling"
                : "Stable"}
            </span>
          </span>
        </div>
        <p style={{ fontSize: "0.78rem", marginTop: 4, marginBottom: "0px" }}>{lamb.comment}</p>
      </div>
    </section>
  );
}

function PolicyCard({ items }) {
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Environmental & Policy Signals</div>
        <div className={styles.cardTag}>Defra / Welsh Gov</div>
      </header>
      <div className={styles.cardBody}>
        <ul className={styles.listCompact}>
          {items.map((item, idx) => (
            <li key={idx}>
              <span>•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function TwinSignalsCard({ data }) {
  const { nutrientBalance, carbonIntensity, pasturePerformance } = data;
  const n = nutrientBalance;
  const c = carbonIntensity;
  const p = pasturePerformance;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>FarmTwin Digital Twin Signals</div>
        <div className={styles.cardTag}>Last 7 days</div>
      </header>
      <div className={styles.cardBody}>
        <p className={styles.subtitle}>Nutrient balance</p>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>N surplus</span>
          <span className={styles.metricValue}>{n.nSurplus}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>P surplus</span>
          <span className={styles.metricValue}>{n.pSurplus}</span>
        </div>
        <p style={{ fontSize: "0.78rem", marginTop: 4, marginBottom: "0px" }}>{n.note}</p>

        <p className={styles.subtitle} style={{ marginTop: 10 }}>
          Carbon intensity
        </p>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Last week</span>
          <span className={styles.metricValue}>{c.lastWeek}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Previous week</span>
          <span className={styles.metricValue}>{c.previousWeek}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Trend</span>
          <span className={styles.metricValue}>
            {c.trend}
            <span className={`${styles.metricBadge} ${styles.metricPillOk}`}>Improving</span>
          </span>
        </div>

        <p className={styles.subtitle} style={{ marginTop: 10 }}>
          Pasture utilisation & performance
        </p>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>Pasture utilisation</span>
          <span className={styles.metricValue}>{p.utilisation}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>LWG deviation</span>
          <span className={styles.metricValue}>{p.lwgDeviation}</span>
        </div>
      </div>
    </section>
  );
}

function DeadlinesCard({ items }) {
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>Deadlines & Administration</div>
        <div className={styles.cardTag}>Next 30 days</div>
      </header>
      <div className={styles.cardBody}>
        <ul className={styles.deadlinesList}>
          {items.map((item) => (
            <li key={item.date} className={styles.deadlineItem}>
              <span className={styles.deadlineDate}>{item.date}</span>
              <span className={styles.deadlineText}>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const FarmTwinDailyDashboard = () => {
  const [meta] = useState(mockMeta);

  const [priorities, setPriorities] = useState(mockPriorities);
  const [weather, setWeather] = useState(mockWeather);
  const [grass, setGrass] = useState(mockGrass);
  const [health, setHealth] = useState(mockHealth);
  const [slurry, setSlurry] = useState(mockSlurry);
  const [market, setMarket] = useState(mockMarket);
  const [policy, setPolicy] = useState(mockPolicy);
  const [twinSignals, setTwinSignals] = useState(mockTwinSignals);
  const [deadlines, setDeadlines] = useState(mockDeadlines);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const farmCode = meta.farmCode;
    const regionCode = "England/Wales";

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [
          summaryRes,
          weatherRes,
          grassRes,
          healthRes,
          slurryRes,
          marketRes,
          policyRes,
          twinRes,
          deadlinesRes,
        ] = await Promise.all([
          getDailySummary(farmCode),
          getWeatherConditions(farmCode),
          getGrassForage(farmCode),
          getHealthRisk(farmCode),
          getSlurryWindow(farmCode),
          getMarket(regionCode),
          getPolicySignals(farmCode),
          getTwinSignals(farmCode),
          getDeadlines(farmCode),
        ]);

        if (summaryRes && Array.isArray(summaryRes.priorities)) {
          setPriorities(summaryRes.priorities);
        }

        if (weatherRes && weatherRes.today) {
          setWeather({
            location: weatherRes.locationLabel ?? weatherRes.location ?? mockWeather.location,
            today: {
              maxTemp:
                (weatherRes.today.maxTempC ?? null) !== null
                  ? `${weatherRes.today.maxTempC}°C`
                  : mockWeather.today.maxTemp,
              minTemp:
                (weatherRes.today.minTempC ?? null) !== null
                  ? `${weatherRes.today.minTempC}°C`
                  : mockWeather.today.minTemp,
              rainMm: weatherRes.today.rainMm ?? mockWeather.today.rainMm,
              wind:
                weatherRes.today.windSpeedMph && weatherRes.today.windDirection
                  ? `${weatherRes.today.windSpeedMph} mph (${weatherRes.today.windDirection})`
                  : mockWeather.today.wind,
              frostRisk:
                weatherRes.today.frostRisk ?? mockWeather.today.frostRisk,
            },
            fieldConditions:
              (weatherRes.fieldConditions ?? mockWeather.fieldConditions).map(
                (f) => ({
                  label: f.label,
                  status: f.status,
                  risk: f.riskLevel ?? f.risk ?? "ok",
                })
              ),
          });
        }

        if (grassRes && typeof grassRes.currentGrowthKgDmPerHaPerDay === "number") {
          setGrass({
            region: grassRes.region ?? mockGrass.region,
            currentGrowth: `${grassRes.currentGrowthKgDmPerHaPerDay} kg DM/ha/day`,
            avg7d: `${grassRes.sevenDayAvgKgDmPerHaPerDay} kg DM/ha/day (Δ ${grassRes.sevenDayDeltaKgDmPerHaPerDay})`,
            dmEstimate: `${grassRes.grassDmPercentEstimate} %`,
            grazingDaysAhead: grassRes.grazingDaysAhead ?? mockGrass.grazingDaysAhead,
            note: grassRes.advisoryNote ?? mockGrass.note,
          });
        }

        if (healthRes && healthRes.region) {
          setHealth({
            region: healthRes.region,
            parasiteRisk: healthRes.parasiteRisk?.description ?? mockHealth.parasiteRisk,
            flukeRiskLevel: healthRes.flukeRisk?.description ?? mockHealth.flukeRiskLevel,
            heatStress:
              healthRes.heatStress?.description ?? mockHealth.heatStress,
            chillRisk: healthRes.chillRisk?.description ?? mockHealth.chillRisk,
          });
        }

        if (slurryRes && slurryRes.nvzStatus) {
          setSlurry({
            nvzStatus: slurryRes.nvzStatus,
            suitability:
              slurryRes.spreadingSuitability?.description ?? mockSlurry.suitability,
            runoffRisk: slurryRes.runoffRisk?.description ?? mockSlurry.runoffRisk,
            storeFillPercent: slurryRes.storeFillPercent ?? mockSlurry.storeFillPercent,
            daysUntilFull:
              slurryRes.daysUntilFullAtCurrentInflow ?? mockSlurry.daysUntilFull,
          });
        }

        if (marketRes && marketRes.beef && marketRes.lamb) {
          const beefRes = marketRes.beef;
          const lambRes = marketRes.lamb;

          const beefLast =
            beefRes.priceHistory && beefRes.priceHistory.length
              ? beefRes.priceHistory[beefRes.priceHistory.length - 1].pricePencePerKg
              : mockMarket.beef.lastPrices[mockMarket.beef.lastPrices.length - 1]
                  .price;

          const beefSeries =
            beefRes.priceHistory?.map((p) => ({
              date: p.date,
              price: p.pricePencePerKg,
            })) ?? mockMarket.beef.lastPrices;

          setMarket({
            beef: {
              classLabel: beefRes.classLabel ?? mockMarket.beef.classLabel,
              lastPrices: beefSeries,
              ma7: beefRes.movingAverage7d ?? mockMarket.beef.ma7,
              forecast: {
                min: beefRes.forecast?.minPencePerKg ?? mockMarket.beef.forecast.min,
                max: beefRes.forecast?.maxPencePerKg ?? mockMarket.beef.forecast.max,
              },
              trend: beefRes.forecast?.trend ?? mockMarket.beef.trend,
              comment: beefRes.comment ?? mockMarket.beef.comment,
              last: beefLast,
            },
            lamb: {
              classLabel: lambRes.classLabel ?? mockMarket.lamb.classLabel,
              lastPrice:
                lambRes.lastPricePencePerKg ?? mockMarket.lamb.lastPrice,
              ma7: lambRes.movingAverage7d ?? mockMarket.lamb.ma7,
              forecast: {
                min: lambRes.forecast?.minPencePerKg ?? mockMarket.lamb.forecast.min,
                max: lambRes.forecast?.maxPencePerKg ?? mockMarket.lamb.forecast.max,
              },
              trend: lambRes.forecast?.trend ?? mockMarket.lamb.trend,
              comment: lambRes.comment ?? mockMarket.lamb.comment,
            },
          });
        }

        if (policyRes && Array.isArray(policyRes.items)) {
          setPolicy(policyRes.items.map((p) => p.text));
        }

        if (twinRes && twinRes.nutrientBalance) {
          const nb = twinRes.nutrientBalance;
          const ci = twinRes.carbonIntensity;
          const pp = twinRes.pasturePerformance;
          setTwinSignals({
            nutrientBalance: {
              nSurplus: nb.nSurplusKgPerHa != null
                ? `${nb.nSurplusKgPerHa} kg N/ha (target ${nb.nSurplusTargetKgPerHa} kg N/ha)`
                : mockTwinSignals.nutrientBalance.nSurplus,
              pSurplus: nb.pSurplusKgPerHa != null
                ? `${nb.pSurplusKgPerHa} kg P/ha (target ${nb.pSurplusTargetKgPerHa} kg P/ha)`
                : mockTwinSignals.nutrientBalance.pSurplus,
              note: nb.advisoryNote ?? mockTwinSignals.nutrientBalance.note,
            },
            carbonIntensity: {
              lastWeek:
                ci.currentKgCo2ePerKgLwg != null
                  ? `${ci.currentKgCo2ePerKgLwg} kg CO₂e/kg LWG`
                  : mockTwinSignals.carbonIntensity.lastWeek,
              previousWeek:
                ci.previousKgCo2ePerKgLwg != null
                  ? `${ci.previousKgCo2ePerKgLwg} kg CO₂e/kg LWG`
                  : mockTwinSignals.carbonIntensity.previousWeek,
              trend: ci.trendText ?? mockTwinSignals.carbonIntensity.trend,
            },
            pasturePerformance: {
              utilisation:
                pp.utilisationPercent != null
                  ? `${pp.utilisationPercent} %`
                  : mockTwinSignals.pasturePerformance.utilisation,
              lwgDeviation:
                pp.lwgDeviationText ??
                `${pp.lwgDeviationPercent} % vs model`,
            },
          });
        }

        if (deadlinesRes && Array.isArray(deadlinesRes.items)) {
          setDeadlines(
            deadlinesRes.items.map((d) => ({
              date: d.date,
              text: d.text,
              severity: d.severity,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load live data, using mocks:", err);
        setError("Using mock data only. Live API calls failed.");
      } finally {
        setLoading(false);
      }
    }

    // Comment this in when you have a back end available.
    // load();
  }, [meta.farmCode]);

  return (
    <div className={styles.appRoot}>
      <div className={styles.appShell}>
        <header className={styles.appHeader}>
          <div className={styles.appHeaderTop}>
            <div className={styles.titleBlock}>
              <h1>x10AI FarmTwin Daily Dashboard</h1>
              <div className={styles.titleSub}>
                Farm {meta.farmCode} · {meta.farmName} · {meta.region}
              </div>
            </div>
            <div className={styles.metaBadgeRow}>
              <span className={`${styles.metaPill} ${styles.pillAccent}`}>
                <span className={styles.metaPillLabel}>System</span>
                <span className={styles.metaPillValue}>{meta.system}</span>
              </span>
              <span className={`${styles.metaPill} ${styles.pillMuted}`}>
                <span className={styles.metaPillLabel}>Mode</span>
                <span className={styles.metaPillValue}>
                  Daily operational view
                </span>
              </span>
              <span className={`${styles.metaPill} ${styles.pillWarning}`}>
                <span className={styles.metaPillLabel}>Status</span>
                <span className={styles.metaPillValue}>
                  Prototype · Mock data only
                </span>
              </span>
            </div>
          </div>
          <PrioritySummary dateLabel={meta.dateLabel} priorities={priorities} />
        </header>

        {loading && (
          <div style={{ fontSize: "0.78rem", color: "#9ba3c2", marginBottom: 8 }}>
            Loading live data&hellip;
          </div>
        )}
        {error && (
          <div style={{ fontSize: "0.78rem", color: "#f97373", marginBottom: 8 }}>
            {error}
          </div>
        )}

        <main className={styles.layoutGrid}>
          <div className={styles.columnStack}>
            <WeatherCard data={weather} />
            <GrassCard data={grass} />
            <HealthCard data={health} />
            <SlurryCard data={slurry} />
          </div>
          <div className={styles.columnStack}>
            <MarketCard data={market} />
            <TwinSignalsCard data={twinSignals} />
            <PolicyCard items={policy} />
            <DeadlinesCard items={deadlines} />
          </div>
        </main>
      </div>
    </div>
  );
}

export default FarmTwinDailyDashboard;